// SCR-SAL-05 Sales register (read-only, ADR-107: Invoices · Receipts), SCR-SAL-02 invoice card (Invoice · Document,
// shared as an image of the document, ADR-108/109) and SCR-SAL-06 Promotions (Active · Scheduled · Inactive).
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Chips,
  EmptyState,
  List,
  ListRow,
  Notice,
  QuickActions,
  SearchField,
  SkeletonList,
  SummaryCard,
  Tabs,
  Tiles,
  VirtualList,
} from '../../design/kit';
import {
  SALES,
  loadCollections,
  loadPromotions,
  loadSale,
  loadSales,
  togglePromotion,
  type Collection,
  type Promotion,
  type Sale,
} from '../../data/sales';
import { loadSettings } from '../../data/settings';
import { Code, Name } from '../../i18n/Isolate';
import { businessLanguage, directionOf, type Language } from '../../i18n';
import { shareDocument } from '../../lib/ux/share';
import { ReverseCollectionSheet } from '../returns/ReturnsPage';
import { businessToday, formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { useSession } from '../../app/session';
import { useCardHeader } from '../../app/Shell';
import { initials, useTab } from '../catalog/common';
import { countWord } from '../stock/StockSheets';
import { BundleParts, PromotionSheet } from './SaleSheets';

function useFmt() {
  const { i18n } = useTranslation();
  const lang = i18n.language as Language;
  return { lang, fmt: (v: number | string) => formatMoney(v, lang) };
}

export function StatusBadge({ sale }: { sale: Sale }) {
  const { t } = useTranslation();
  if (sale.split_with)
    return (
      <Badge tone={sale.status === 'paid' ? 'info' : 'warning'}>
        {t(sale.status === 'paid' ? 'st.splitpart' : 'st.unpaidi')}
      </Badge>
    );
  return (
    <Badge tone={sale.status === 'paid' ? 'success' : 'warning'}>
      {t(sale.status === 'paid' ? 'st.paid' : 'st.unpaidi')}
    </Badge>
  );
}

/** Invoice rows shared by the Round page, the register and the customer card. */
export function InvoiceRows({ sales }: { sales: Sale[] }) {
  const { t } = useTranslation();
  const { role } = useSession();
  const { fmt, lang } = useFmt();
  const navigate = useNavigate();
  return (
    <VirtualList
      label={t('tab.invoices')}
      items={sales}
      rowKey={(s) => s.id}
      row={(s) => (
        <ListRow
          testId={`inv-${s.number}`}
          lead={{ initials: initials(s.customer) }}
          title={<Name>{s.customer}</Name>}
          onOpen={() => navigate(`/sales/${s.id}`)}
          sub={
            <>
              <Code>{s.number}</Code> · <bdi className="yv-nowrap">{formatDateTime(s.at, lang)}</bdi> ·{' '}
              {t(s.lines.length === 1 ? 'lines.one' : 'lines.n', { n: s.lines.length })}
              {role === 'owner' && s.margin !== null && ` · ${t('iv.margin', { a: fmt(s.margin) })}`}
              {s.status === 'unpaid' && s.collector_id !== s.seller_id && s.collector_name && (
                <> · {t('iv.heldby', { n: s.collector_name })}</>
              )}
            </>
          }
          value={fmt(s.total)}
          badge={
            <>
              <StatusBadge sale={s} />
              {s.price_changed && <Badge tone="warning">{t('sl.changed')}</Badge>}
            </>
          }
        />
      )}
    />
  );
}

/** Receipts; the owner's register adds "Reverse" on each one not yet reversed (SAL-08). */
export function CollectionRows({
  collections,
  onReverse,
}: {
  collections: Collection[];
  onReverse?: ((c: Collection) => void) | undefined;
}) {
  const { t } = useTranslation();
  const { fmt, lang } = useFmt();
  return (
    <VirtualList
      label={t('tab.receiptsr')}
      items={collections}
      rowKey={(c) => c.id}
      row={(c) => (
        <ListRow
          testId={`col-${c.number}`}
          lead={{ icon: 'cash' }}
          title={<Name>{c.customer}</Name>}
          sub={
            <>
              <Code>{c.number}</Code> · <bdi className="yv-nowrap">{formatDateTime(c.at, lang)}</bdi> ·{' '}
              {t('rc.invoices', { n: c.invoices.join(', ') })}
              {c.seller_name && (
                <>
                  {' · '}
                  {t('rc.by', { n: c.seller_name })}
                </>
              )}
            </>
          }
          value={fmt(c.amount)}
          badge={c.reversed ? <Badge>{t('rv.reversed')}</Badge> : undefined}
          actions={
            onReverse && !c.reversed ? (
              <Button pill onClick={() => onReverse(c)}>
                {t('act.reverse')}
              </Button>
            ) : undefined
          }
        />
      )}
    />
  );
}

const TABS = ['invoices', 'receiptsr'] as const;
type Tab = (typeof TABS)[number];
const PERIODS = ['f.today', 'f.week', 'f.month'] as const;
const STATUSES = ['f.all', 'f.paid', 'f.unpaid', 'f.changed'] as const;

function since(period: (typeof PERIODS)[number]): string {
  const d = new Date(`${businessToday()}T00:00:00Z`);
  if (period === 'f.week') d.setUTCDate(d.getUTCDate() - 6);
  if (period === 'f.month') d.setUTCDate(1);
  return d.toISOString().slice(0, 10);
}

/** SCR-SAL-05: the read-only register; a Seller sees only their own invoices (RLS). */
export function SalesPage() {
  const { t } = useTranslation();
  const { role } = useSession();
  const { fmt } = useFmt();
  const [tab, setTab] = useTab(TABS, 'invoices');
  const [period, setPeriod] = useState<(typeof PERIODS)[number]>('f.month');
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('f.all');
  const [text, setText] = useState('');
  const [reversing, setReversing] = useState<Collection | null>(null);
  const sales = useQuery({
    queryKey: [...SALES, 'register', period, status],
    queryFn: () =>
      loadSales({
        from: since(period),
        status: status === 'f.paid' ? 'paid' : status === 'f.unpaid' ? 'unpaid' : undefined,
        changed: status === 'f.changed' || undefined,
      }),
  });
  const collections = useQuery({
    queryKey: [...SALES, 'collections'],
    queryFn: () => loadCollections(),
    enabled: tab === 'receiptsr',
  });
  const q = text.trim().toLowerCase();
  const list = (sales.data ?? []).filter(
    (s) => !q || s.customer.toLowerCase().includes(q) || s.number.toLowerCase().includes(q),
  );
  const value = list.reduce((a, s) => a + Number(s.total), 0);
  const unpaid = list.filter((s) => s.status === 'unpaid');
  return (
    <>
      <SummaryCard
        label={t('reg.value')}
        value={fmt(value)}
        figures={[
          { label: t('reg.unpaid'), value: fmt(unpaid.reduce((a, s) => a + Number(s.total), 0)) },
          { label: t('reg.unpaidn'), value: String(unpaid.length) },
        ]}
      />
      <Tabs
        label={t('p.sales')}
        value={tab}
        onChange={(v) => setTab(v as Tab)}
        items={TABS.map((id) => ({ id, label: t(`tab.${id}`) }))}
      />
      {tab === 'invoices' ? (
        <>
          <div className="yv-filters">
            <SearchField value={text} onChange={setText} label={t('search.invoices')} />
            <Chips
              label={t('f.month')}
              items={PERIODS.map((k) => ({ id: k, label: t(k) }))}
              selected={[period]}
              onToggle={(k) => setPeriod(k as (typeof PERIODS)[number])}
            />
            <Chips
              label={t('f.all')}
              items={STATUSES.filter((k) => k !== 'f.changed' || role === 'owner').map((k) => ({ id: k, label: t(k) }))}
              selected={[status]}
              onToggle={(k) => setStatus(k as (typeof STATUSES)[number])}
            />
          </div>
          {!sales.data ? (
            <SkeletonList label={t('loading')} />
          ) : list.length === 0 ? (
            <EmptyState icon="file" title={t('empty.invoices')} text={t('empty.invoices.text')} />
          ) : (
            <InvoiceRows sales={list} />
          )}
        </>
      ) : !collections.data ? (
        <SkeletonList label={t('loading')} />
      ) : (
        <CollectionRows collections={collections.data} onReverse={role === 'owner' ? setReversing : undefined} />
      )}
      {reversing && <ReverseCollectionSheet collection={reversing} onClose={() => setReversing(null)} />}
    </>
  );
}

const CARD_TABS = ['invoice', 'document'] as const;
type CardTab = (typeof CARD_TABS)[number];

/** SCR-SAL-02: one invoice and its paper document (PaperDoc, ADR-108). */
export function InvoiceCard() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const { role } = useSession();
  const { fmt, lang } = useFmt();
  const navigate = useNavigate();
  const [tab, setTab] = useTab(CARD_TABS, 'invoice');
  const [shared, setShared] = useState<string>();
  const paper = useRef<HTMLElement>(null);
  const sale = useQuery({ queryKey: [...SALES, 'one', id], queryFn: () => loadSale(id) });
  const settings = useQuery({ queryKey: ['settings', 'v_settings'], queryFn: loadSettings });
  const s = sale.data;
  const back = useCallback(() => navigate(-1), [navigate]);
  useCardHeader(s ? s.number : t('p.sales'), back);
  if (sale.isLoading) return <SkeletonList label={t('loading')} />;
  if (!s) return <Notice tone="warning">{t('err.notfound')}</Notice>;
  // ADR-110: the document speaks the business language, whatever language this person reads the app in.
  const dl = businessLanguage();
  const dt = i18n.getFixedT(dl);
  const dfmt = (v: number | string) => formatMoney(v, dl);
  const text = `${dt('sale.share', { n: s.number, c: s.customer, a: dfmt(s.total) })}\n${s.lines
    .map((l) => `${l.product} · ${countWord(dt, l.unit, l.quantity)} · ${dfmt(l.net)}`)
    .join('\n')}`;
  // ADR-109: the paper document goes out as an image; text only when no image can be made or shared.
  const share = async () => {
    try {
      if (paper.current) {
        await shareDocument(
          paper.current,
          s.number,
          s.number,
          dt('sale.share', { n: s.number, c: s.customer, a: dfmt(s.total) }),
        );
        return;
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
    }
    try {
      if (navigator.share) await navigator.share({ title: s.number, text });
      else {
        await navigator.clipboard.writeText(text);
        setShared(text);
      }
    } catch {
      setShared(text);
    }
  };
  return (
    <>
      <SummaryCard
        label={s.customer}
        value={fmt(s.total)}
        sub={
          <>
            {t(s.status === 'paid' ? 'st.paid' : 'st.unpaidi')} ·{' '}
            <bdi className="yv-nowrap">{formatDateTime(s.at, lang)}</bdi> · <Code>{s.truck_code}</Code> ·{' '}
            {s.seller_name ? <Name>{s.seller_name}</Name> : ''} · <Code>{s.round_number}</Code>
          </>
        }
      />
      <Tabs
        label={s.number}
        value={tab}
        onChange={(v) => setTab(v as CardTab)}
        items={CARD_TABS.map((v) => ({ id: v, label: t(`tab.${v}`) }))}
      />
      {tab === 'invoice' ? (
        <>
          <List label={t('tab.invoice')}>
            {s.lines.map((l) => (
              <ListRow
                key={l.id}
                title={<Name>{l.product}</Name>}
                sub={
                  <>
                    {countWord(t, l.unit, l.quantity)} × {fmt(l.unit_price)}
                    <BundleParts product={l.product_id} quantity={l.quantity} />
                    {l.price_changed && (
                      <>
                        {' '}
                        <s className="muted">{t('sl.catalogue', { p: fmt(l.catalogue_price) })}</s>
                      </>
                    )}
                    {l.promotion && (
                      <>
                        <br />
                        <Badge tone={l.promo_applied ? 'info' : 'neutral'}>
                          {l.promo_applied
                            ? t('sl.promo', { p: l.promo_pct ?? 0, n: l.promotion })
                            : t('sl.promo.removed')}
                        </Badge>
                      </>
                    )}
                    {Number(l.line_discount) > 0 && ` · ${t('sl.linedisc')} ${fmt(l.line_discount)}`}
                  </>
                }
                value={fmt(l.net)}
                badge={l.price_changed ? <Badge tone="warning">{t('sl.changed')}</Badge> : undefined}
              />
            ))}
          </List>
          {role === 'owner' && s.cost !== null && s.margin !== null && (
            <p className="muted">
              {t('iv.cost', { a: fmt(s.cost) })} · {t('iv.margin', { a: fmt(s.margin) })}
            </p>
          )}
          <List label={t('sl.total')}>
            <ListRow title={t('sl.gross')} value={fmt(s.gross)} />
            <ListRow title={t('sl.promos')} value={fmt(s.promo_total)} />
            <ListRow title={t('sl.discounts')} value={fmt(s.discount_total)} />
            <ListRow title={<b>{t('sl.total')}</b>} value={<b>{fmt(s.total)}</b>} />
          </List>
          {s.split_number && <Notice tone="info">{t('iv.split', { n: s.split_number })}</Notice>}
          {s.collection_number && <Notice tone="info">{t('iv.paidby', { n: s.collection_number })}</Notice>}
        </>
      ) : (
        <>
          <article className="yv-paper" data-testid="paper" ref={paper} lang={dl} dir={directionOf(dl)}>
            <header className="yv-paper__head">
              <div>
                <div className="yv-paper__brand">{settings.data?.business_name ?? 'YakVender'}</div>
                <div>{dt('doc.address')}</div>
              </div>
              <div className="yv-paper__meta">
                {dt('doc.sale')}
                <br />
                <Code>{s.number}</Code>
                <br />
                {formatDateTime(s.at, dl)}
              </div>
            </header>
            <div className="yv-paper__party">
              {dt('doc.customer')}: <Name>{s.customer}</Name> · <Code>{s.truck_code}</Code> ·{' '}
              {s.seller_name ? <Name>{s.seller_name}</Name> : ''}
            </div>
            <div className="yv-paper__scroll">
              <table>
                <thead>
                  <tr>
                    <th>{dt('doc.product')}</th>
                    <th className="num">{dt('doc.qty')}</th>
                    <th className="num">{dt('doc.price')}</th>
                    <th className="num">{dt('doc.net')}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.lines.map((l) => (
                    <tr key={l.id}>
                      <td>
                        <Name>{l.product}</Name>
                        <BundleParts product={l.product_id} quantity={l.quantity} />
                      </td>
                      <td className="num">{countWord(dt, l.unit, l.quantity)}</td>
                      <td className="num">{dfmt(l.unit_price)}</td>
                      <td className="num">{dfmt(l.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="yv-paper__totals">
              <span>
                {dt('sl.discounts')} {dfmt(s.discount_total)}
              </span>
              <b>
                {dt('sl.total')} {dfmt(s.total)}
              </b>
            </div>
            <div className="yv-paper__stamp">{dt(s.status === 'paid' ? 'st.paid' : 'st.unpaidi')}</div>
          </article>
          <QuickActions
            actions={[{ id: 'share', label: t('act.share'), icon: 'file', main: true, onSelect: () => void share() }]}
          />
          {shared && <pre className="muted yv-pre">{shared}</pre>}
        </>
      )}
    </>
  );
}

const PROMO_TABS = ['prmactive', 'prmscheduled', 'prmpast'] as const;
type PromoTab = (typeof PROMO_TABS)[number];

/** SCR-SAL-06: promotion cards; pause and resume, never delete (PRM-01, PRM-02). */
export function PromotionsPage() {
  const { t } = useTranslation();
  const { fmt, lang } = useFmt();
  const qc = useQueryClient();
  const [tab, setTab] = useTab(PROMO_TABS, 'prmactive');
  const [sheet, setSheet] = useState<Promotion | 'new' | null>(null);
  const promos = useQuery({ queryKey: [...SALES, 'promotions'], queryFn: loadPromotions });
  const toggle = useMutation({
    mutationFn: (p: Promotion) => togglePromotion(p.id, !p.paused),
    onSuccess: () => void qc.invalidateQueries({ queryKey: SALES }),
  });
  const shown = (promos.data ?? []).filter((p) =>
    tab === 'prmactive'
      ? p.status === 'active'
      : tab === 'prmscheduled'
        ? p.status === 'scheduled'
        : p.status === 'paused' || p.status === 'expired',
  );
  return (
    <>
      <QuickActions
        actions={[{ id: 'new', label: t('act.newpromo'), icon: 'plus', main: true, onSelect: () => setSheet('new') }]}
      />
      <Tabs
        label={t('p.promotions')}
        value={tab}
        onChange={(v) => setTab(v as PromoTab)}
        items={PROMO_TABS.map((id) => ({ id, label: t(`tab.${id}`) }))}
      />
      {!promos.data ? (
        <SkeletonList label={t('loading')} />
      ) : shown.length === 0 ? (
        <EmptyState icon="tag" title={t(`tab.${tab}`)} text={t('pm.allprod')} />
      ) : (
        <Tiles label={t(`tab.${tab}`)}>
          {shown.map((p) => (
            <article key={p.id} className="yv-tile yv-tile--card yv-promo" data-testid={`promo-${p.name}`}>
              <div className="yv-promo__head">
                <b className="yv-promo__pct">{Number(p.pct)}%</b>
                <Badge tone={p.status === 'active' ? 'success' : p.status === 'scheduled' ? 'info' : 'neutral'}>
                  {t(`pm.${p.status}`)}
                </Badge>
              </div>
              <button type="button" className="yv-row__open yv-tile__name" onClick={() => setSheet(p)}>
                <Name>{p.name}</Name>
              </button>
              <div className="yv-tile__sub">
                {p.condition === 'qty'
                  ? t('pm.from.qty', { n: Number(p.minimum) })
                  : t('pm.from.amount', { a: fmt(p.minimum) })}{' '}
                · {p.product ? <Name>{p.product}</Name> : t('pm.allprod')} ·{' '}
                {p.group_name ? <Name>{p.group_name}</Name> : t('pm.allcust')}
                <br />
                <bdi className="yv-nowrap">{formatDate(p.start_date, lang)}</bdi> →{' '}
                <bdi className="yv-nowrap">{formatDate(p.end_date, lang)}</bdi> · {t('pm.priority', { n: p.priority })}
              </div>
              {p.status !== 'expired' && (
                <Button pill busy={toggle.isPending} onClick={() => toggle.mutate(p)}>
                  {t(p.paused ? 'act.resume' : 'act.pause')}
                </Button>
              )}
            </article>
          ))}
        </Tiles>
      )}
      {sheet && <PromotionSheet promotion={sheet === 'new' ? undefined : sheet} onClose={() => setSheet(null)} />}
    </>
  );
}
